import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import gun from '../config/gun.config';
import { StabeonEvent, REACTION_KEYS, ReactionEmoji } from '../types/event';
import { ActivityCategory, AvatarConfig } from '../types/user';

export function useEvents() {
  const [events, setEvents] = useState<Record<string, StabeonEvent>>({});
  const pendingRef = useRef<Record<string, StabeonEvent>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cache signatures to skip GUN re-emissions of unchanged events
  const sigRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const node = gun.get('stabeon_events');
    const mapNode = node.map();

    mapNode.on((data: any, id: string) => {
      if (!data || !id || !data.createdAt) return;

      // Skip if data hasn't actually changed
      const sig = `${data.title}|${data.description}|${data.startsAt}|${data.endsAt}|${data.reactions_fire||0}|${data.reactions_heart||0}|${data.reactions_skull||0}|${data.reactions_hundred||0}|${data.reactions_eyes||0}|${data.attendeeCount||0}|${data.commentCount||0}`;
      if (sigRef.current[id] === sig) return;
      sigRef.current[id] = sig;

      const event: StabeonEvent = {
        ...data,
        id,
        creatorAvatar: data.creatorAvatar
          ? (typeof data.creatorAvatar === 'string'
            ? (() => { try { return JSON.parse(data.creatorAvatar); } catch { return undefined; } })()
            : data.creatorAvatar)
          : undefined,
        reactions_fire: data.reactions_fire || 0,
        reactions_heart: data.reactions_heart || 0,
        reactions_skull: data.reactions_skull || 0,
        reactions_hundred: data.reactions_hundred || 0,
        reactions_eyes: data.reactions_eyes || 0,
        attendeeCount: data.attendeeCount || 0,
        commentCount: data.commentCount || 0,
      };

      pendingRef.current[id] = event;
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          const batch = pendingRef.current;
          pendingRef.current = {};
          setEvents(prev => Object.assign({}, prev, batch));
        }, 50);
      }
    });

    return () => {
      try { mapNode.off(); } catch (_) {}
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, []);

  // Only show events that haven't ended
  const activeEvents = useMemo(
    () => Object.values(events).filter(e => e.endsAt > Date.now()).sort((a, b) => a.startsAt - b.startsAt),
    [events]
  );

  const pastEvents = useMemo(
    () => Object.values(events).filter(e => e.endsAt <= Date.now()).sort((a, b) => b.startsAt - a.startsAt),
    [events]
  );

  const createEvent = useCallback((
    lat: number,
    lng: number,
    title: string,
    description: string,
    category: ActivityCategory,
    startsAt: number,
    endsAt: number,
    creatorId: string,
    creatorNick: string,
    creatorAvatar?: AvatarConfig,
    imageData?: string,
  ) => {
    const eventData = {
      creatorId,
      creatorNick,
      creatorAvatar: creatorAvatar ? JSON.stringify(creatorAvatar) : null,
      lat, lng,
      title: title.trim(),
      description: description.trim(),
      category,
      imageData: imageData || null,
      startsAt,
      endsAt,
      createdAt: Date.now(),
      attendeeCount: 1, // creator auto-joins
      commentCount: 0,
      reactions_fire: 0,
      reactions_heart: 0,
      reactions_skull: 0,
      reactions_hundred: 0,
      reactions_eyes: 0,
    };
    gun.get('stabeon_events').set(eventData as any);
  }, []);

  const rsvpEvent = useCallback((
    eventId: string,
    userId: string,
    nickname: string,
    avatar?: AvatarConfig,
    knownEvent?: { maxAttendees?: number; attendeeCount: number },
  ) => {
    const eventNode = gun.get('stabeon_events').get(eventId);
    const userField = `rsvp_${userId}`;

    eventNode.get(userField).once((val: any) => {
      if (val) {
        // Leave event
        eventNode.get(userField).put(null);
        eventNode.get('attendeeCount').once((count: any) => {
          eventNode.get('attendeeCount').put(Math.max(0, (count || 1) - 1));
        });
      } else {
        const maxAttendees = knownEvent?.maxAttendees || 0;
        const currentCount = knownEvent?.attendeeCount || 0;

        if (maxAttendees > 0 && currentCount >= maxAttendees) {
          // Event full — join waitlist instead
          eventNode.get(`waitlist_${userId}`).once((wval: any) => {
            if (!wval) {
              eventNode.get(`waitlist_${userId}`).put(true);
              eventNode.get('waitlistCount').once((wcount: any) => {
                eventNode.get('waitlistCount').put((wcount || 0) + 1);
              });
            }
          });
        } else {
          // Join event
          eventNode.get(userField).put(true);
          eventNode.get('attendeeCount').once((count: any) => {
            eventNode.get('attendeeCount').put((count || 0) + 1);
          });
          // Add to attendees list
          eventNode.get('attendees').set({
            userId,
            nickname,
            avatar: avatar ? JSON.stringify(avatar) : null,
            rsvpAt: Date.now(),
          } as any);
          // Update attendeePreview (first 5 avatars stored inline on event)
          if (avatar && currentCount < 5) {
            eventNode.get('attendeePreview').once((preview: any) => {
              let arr: { e: string; b: string; u: string }[] = [];
              if (preview && typeof preview === 'string') {
                try { arr = JSON.parse(preview); } catch {}
              }
              if (!arr.some(a => a.u === userId)) {
                arr.push({ e: avatar.emoji, b: avatar.bgColor, u: userId });
                eventNode.get('attendeePreview').put(JSON.stringify(arr.slice(0, 5)));
              }
            });
          }
        }
      }
    });
  }, []);

  const hasUserRSVP = useCallback((eventId: string, userId: string): Promise<boolean> => {
    return new Promise(resolve => {
      const t = setTimeout(() => resolve(false), 5000);
      gun.get('stabeon_events').get(eventId).get(`rsvp_${userId}`).once((val: any) => {
        clearTimeout(t);
        resolve(!!val);
      });
    });
  }, []);

  const hasUserOnWaitlist = useCallback((eventId: string, userId: string): Promise<boolean> => {
    return new Promise(resolve => {
      const t = setTimeout(() => resolve(false), 5000);
      gun.get('stabeon_events').get(eventId).get(`waitlist_${userId}`).once((val: any) => {
        clearTimeout(t);
        resolve(!!val);
      });
    });
  }, []);

  const reactToEvent = useCallback((eventId: string, emoji: ReactionEmoji, userId: string) => {
    const key = REACTION_KEYS[emoji];
    const reactionField = `reactions_${key}`;
    const userReactedField = `reacted_${key}_${userId}`;
    const eventNode = gun.get('stabeon_events').get(eventId);

    eventNode.get(userReactedField).once((val: any) => {
      if (val) {
        eventNode.get(reactionField).once((count: any) => {
          eventNode.get(reactionField).put(Math.max(0, (count || 1) - 1));
        });
        eventNode.get(userReactedField).put(null);
      } else {
        eventNode.get(reactionField).once((count: any) => {
          eventNode.get(reactionField).put((count || 0) + 1);
        });
        eventNode.get(userReactedField).put(true);
      }
    });
  }, []);

  const hasUserReactedEvent = useCallback((eventId: string, emoji: ReactionEmoji, userId: string): Promise<boolean> => {
    return new Promise(resolve => {
      const key = REACTION_KEYS[emoji];
      gun.get('stabeon_events').get(eventId).get(`reacted_${key}_${userId}`).once((val: any) => {
        resolve(!!val);
      });
    });
  }, []);

  const updateEvent = useCallback((
    eventId: string,
    creatorId: string,
    myId: string,
    updates: { title?: string; description?: string; category?: ActivityCategory; startsAt?: number; endsAt?: number; venueName?: string; locationName?: string; price?: string; ticketUrl?: string; tags?: string; maxAttendees?: number },
  ) => {
    if (creatorId !== myId) return;
    gun.get('stabeon_events').get(eventId).put(updates as any);
    setEvents(prev => prev[eventId] ? { ...prev, [eventId]: { ...prev[eventId], ...updates } } : prev);
  }, []);

  const deleteEvent = useCallback((eventId: string, creatorId: string, myId: string) => {
    if (creatorId !== myId) return;
    gun.get('stabeon_events').get(eventId).put({ endsAt: 0 } as any);
    setEvents(prev => {
      const next = { ...prev };
      delete next[eventId];
      return next;
    });
  }, []);

  const applyCoOrganizer = useCallback((eventId: string, userId: string, nickname: string) => {
    gun.get('stabeon_events').get(eventId).get(`coorg_apply_${userId}`).put({
      userId, nickname, appliedAt: Date.now(),
    } as any);
  }, []);

  const approveCoOrganizer = useCallback((eventId: string, userId: string, creatorId: string, myId: string) => {
    if (creatorId !== myId) return;
    const eventNode = gun.get('stabeon_events').get(eventId);
    // Mark as approved
    eventNode.get(`coorg_${userId}`).put(true);
    // Remove pending application
    eventNode.get(`coorg_apply_${userId}`).put(null);
    // Update co-organizers JSON list on event root
    eventNode.get('coOrganizers').once((val: any) => {
      let arr: string[] = [];
      if (val && typeof val === 'string') { try { arr = JSON.parse(val); } catch {} }
      if (!arr.includes(userId)) {
        arr.push(userId);
        eventNode.get('coOrganizers').put(JSON.stringify(arr));
      }
    });
  }, []);

  const rejectCoOrganizer = useCallback((eventId: string, userId: string, creatorId: string, myId: string) => {
    if (creatorId !== myId) return;
    gun.get('stabeon_events').get(eventId).get(`coorg_apply_${userId}`).put(null);
  }, []);

  const isCoOrganizer = useCallback((event: { coOrganizers?: string }, userId: string): boolean => {
    if (!event.coOrganizers) return false;
    try { return (JSON.parse(event.coOrganizers) as string[]).includes(userId); }
    catch { return false; }
  }, []);

  return { events: activeEvents, pastEvents, createEvent, updateEvent, rsvpEvent, hasUserRSVP, hasUserOnWaitlist, reactToEvent, hasUserReactedEvent, deleteEvent, applyCoOrganizer, approveCoOrganizer, rejectCoOrganizer, isCoOrganizer };
}
